import * as THREE from 'three';

export type JerseyConfig = {
  name: string;
  number: string;
};

/** Kit palette — white / navy / gold (reads clean on phone OLED too) */
export const KIT = {
  white: '#f7f9fc',
  navy: '#071a42',
  gold: '#d4af37',
  ink: '#071a42',
};

export function jerseyFromProfile(profile: {
  name?: string;
  number?: number;
}): JerseyConfig {
  return {
    name: (profile.name || 'PLAYER').slice(0, 12).toUpperCase(),
    number: String(
      Math.max(1, Math.min(99, Number(profile.number) || 7)),
    ),
  };
}

function makeCanvas(w: number, h: number) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { alpha: true })!;
  ctx.clearRect(0, 0, w, h);
  return { canvas, ctx };
}

function toTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 16;
  tex.flipY = true;
  tex.premultiplyAlpha = false;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

function paintGlyph(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  font: string,
  strokeWidth: number,
  fill = KIT.navy,
  stroke = KIT.gold,
  widthScale = 0.72,
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(widthScale, 1);
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.miterLimit = 2;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = strokeWidth;
  ctx.strokeText(text, 0, 0);
  ctx.fillStyle = fill;
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

/** Name — square map like the number (Decals are reliable with power-of-two square). */
export function createNamePlateTexture(name: string): THREE.CanvasTexture {
  const w = 1024;
  const h = 1024;
  const { canvas, ctx } = makeCanvas(w, h);
  const label = name.slice(0, 12).toUpperCase() || 'PLAYER';

  // Taller glyphs in the center band — less vertical squash on the cloth Decal
  let namePx = 400;
  const nameFont = () =>
    `bold ${namePx}px Impact, "Arial Black", Arial, sans-serif`;
  ctx.font = nameFont();
  const nameWidthScale = 0.72;
  while (
    namePx > 100 &&
    ctx.measureText(label).width * nameWidthScale > w * 0.9
  ) {
    namePx -= 10;
    ctx.font = nameFont();
  }
  paintGlyph(
    ctx,
    label,
    w * 0.5,
    h * 0.5,
    nameFont(),
    Math.max(24, Math.round(namePx * 0.14)),
    KIT.navy,
    KIT.gold,
    nameWidthScale,
  );

  const tex = toTexture(canvas);
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/** Number — navy fill, gold outline. */
export function createNumberPlateTexture(number: string): THREE.CanvasTexture {
  const w = 1024;
  const h = 1024;
  const { canvas, ctx } = makeCanvas(w, h);
  const label = number || '10';
  paintGlyph(
    ctx,
    label,
    w * 0.5,
    h * 0.54,
    'bold 760px Impact, "Arial Black", Arial, sans-serif',
    52,
  );
  const tex = toTexture(canvas);
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/** Collar band strip — navy + gold edge (projected as a thin Decal). */
export function createCollarBandTexture(): THREE.CanvasTexture {
  const w = 1024;
  const h = 128;
  const { canvas, ctx } = makeCanvas(w, h);
  ctx.fillStyle = KIT.navy;
  ctx.fillRect(0, 16, w, h - 32);
  ctx.fillStyle = KIT.gold;
  ctx.fillRect(0, 8, w, 10);
  ctx.fillRect(0, h - 18, w, 10);
  return toTexture(canvas);
}

/** Sleeve cuff — softer navy (less black in sleeve folds) + thin gold stripe. */
export function createCuffTexture(): THREE.CanvasTexture {
  const w = 512;
  const h = 256;
  const { canvas, ctx } = makeCanvas(w, h);
  ctx.fillStyle = '#1a3366';
  ctx.fillRect(0, 56, w, 96);
  ctx.fillStyle = KIT.gold;
  ctx.fillRect(0, 152, w, 16);
  return toTexture(canvas);
}

/**
 * Full kit albedo for the human GLB shirt mesh (Ropa).
 * White body, navy side panels, name + number for identity.
 */
export function createPlayerKitMap(
  name: string,
  number: string,
  opacity = 1,
): THREE.CanvasTexture {
  const w = 1024;
  const h = 1024;
  const { canvas, ctx } = makeCanvas(w, h);

  ctx.fillStyle = KIT.white;
  ctx.fillRect(0, 0, w, h);

  const side = ctx.createLinearGradient(0, 0, w * 0.22, 0);
  side.addColorStop(0, 'rgba(11,31,74,0.55)');
  side.addColorStop(1, 'rgba(11,31,74,0)');
  ctx.fillStyle = side;
  ctx.fillRect(0, 0, w * 0.22, h);
  const sideR = ctx.createLinearGradient(w, 0, w * 0.78, 0);
  sideR.addColorStop(0, 'rgba(11,31,74,0.55)');
  sideR.addColorStop(1, 'rgba(11,31,74,0)');
  ctx.fillStyle = sideR;
  ctx.fillRect(w * 0.78, 0, w * 0.22, h);

  ctx.fillStyle = KIT.gold;
  ctx.fillRect(w * 0.28, h * 0.06, w * 0.44, 10);
  ctx.fillStyle = KIT.navy;
  ctx.fillRect(w * 0.28, h * 0.06 + 10, w * 0.44, 18);

  ctx.globalAlpha = Math.max(0, Math.min(1, opacity));
  const label = (name || '').slice(0, 12).toUpperCase();
  const num = number || '10';
  if (label) {
    paintGlyph(
      ctx,
      label,
      w * 0.5,
      h * 0.33,
      'bold 108px Impact, "Arial Black", Arial, sans-serif',
      16,
    );
  }
  paintGlyph(
    ctx,
    num,
    w * 0.5,
    h * 0.54,
    'bold 320px Impact, "Arial Black", Arial, sans-serif',
    32,
  );
  ctx.globalAlpha = 1;

  const tex = toTexture(canvas);
  tex.flipY = false;
  return tex;
}
